import React, { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { taskAPI, listAPI, timerAPI, timelineAPI } from '../lib/api';
import api from '../lib/api';
import { Layout } from '../components/Layout';
import { TaskModal } from '../components/TaskModal';
import { TaskDetailFullModal } from '../components/TaskDetailFullModal';
import SpaceChannelSlideOver from '../components/SpaceChannelSlideOver';
import CreateSpaceChannelModal from '../components/CreateSpaceChannelModal';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Link } from 'react-router-dom';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { 
    cn, 
    formatDate, 
    priorityConfig, 
    isOverdue,
    getInitials,
    formatDuration
} from '../lib/utils';
import {
    Plus,
    LayoutGrid,
    List,
    GanttChart,
    Filter,
    ChevronDown,
    Clock,
    Play,
    Calendar,
    CheckCircle2,
    Settings2,
    Trash2,
    Edit2,
    ListTodo,
    Loader2,
    UserPlus,
    PanelRightClose,
    Square,
    MessageSquare,
    Hash,
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addDays, differenceInDays, parseISO, subDays } from 'date-fns';

import { useTimerContext } from '@/context/TimerContext';

export default function TasksPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { 
        currentWorkspace, 
        currentSpace, 
        spaces, 
        lists, 
        members,
        selectSpace,
        setLists,
        fetchWorkspaces,
        selectWorkspace,
        workspaces,
        isReadOnly,
        canEditTasks,
        currentUserRole
    } = useWorkspace();
    
    const [viewMode, setViewMode] = useState('list');
    const [selectedList, setSelectedList] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [filterPriority, setFilterPriority] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterAssignee, setFilterAssignee] = useState('all');
    const [initializing, setInitializing] = useState(true);
    
    // Active timer state - for highlighting task with running timer
    const {activeTimer, setActiveTimer} = useTimerContext();
    
    // Full modal state for detail view
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    
    // Split-screen panel state (legacy - keeping for board view)
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [isCreatingTask, setIsCreatingTask] = useState(false);
    
    // deleted Task
    const [tasks, setTasks] = useState([]);

    
    const refreshDeletedTasks = useCallback(async () => {
        if (currentSpace) {
            const response = await taskAPI.getDeletedTaskBySpace(currentSpace.id);
            setTasks(response.data);
        }
    }, [currentSpace]);


    // Auto-select first workspace if none selected
    useEffect(() => {
        const initWorkspace = async () => {
            if (!currentWorkspace) {
                const ws = await fetchWorkspaces();
                if (ws.length > 0) {
                    await selectWorkspace(ws[0]);
                }
            }
            setInitializing(false);
        };
        initWorkspace();
    }, []);

    useEffect(() => {
        // Check for space param in URL
        const spaceId = searchParams.get('space');
        if (spaceId && spaces.length > 0) {
            const space = spaces.find(s => s.id === spaceId);
            if (space) selectSpace(space);
        } else if (!currentSpace && spaces.length > 0) {
            // Auto-select first space if none selected
            selectSpace(spaces[0]);
        }
        
        // Check for task param in URL
        const taskId = searchParams.get('task');
        if (taskId) {
            loadTaskAndOpen(taskId);
        }
    }, [searchParams, spaces, currentSpace]);

    useEffect(() => {
        if (currentSpace && lists.length > 0) {
            setSelectedList(lists[0]);
            refreshDeletedTasks();
        }
    }, [currentSpace, lists]);


    // Check for active timer on mount and periodically
    // useEffect(() => {
    //     checkActiveTimer();
    // }, []);

    const loadTaskAndOpen = async (taskId) => {
        try {
            const response = await taskAPI.getDeletedTask(taskId);
            setSelectedTask(response.data);
            setIsDetailModalOpen(true);
        } catch (error) {
            toast.error('Task not found');
        }
    };

    const handleTaskClick = (task) => {
        setSelectedTask(task);
        setIsDetailModalOpen(true);
        setSearchParams({ task: task.id });
    };

    const handleDetailModalClose = () => {
        setIsDetailModalOpen(false);
        setSelectedTask(null);
        setSearchParams({});
    };

    // Keep modal for backward compatibility
    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedTask(null);
        setSearchParams({});
        refreshDeletedTasks();
    };

    const handleStartTimer = async (e, task) => {
        e.stopPropagation();
        try {
            const response = await timerAPI.start(task.id);
            setActiveTimer(response.data);
            toast.success(`Timer started for "${task.title}"`);
            refreshDeletedTasks();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to start timer');
        }
    };

    const handleStopTimer = async (e, task) => {
        e.stopPropagation();
        try {
            await timerAPI.stop();
            setActiveTimer(null);
            toast.success('Timer stopped');
            refreshDeletedTasks();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to stop timer');
        }
    };

    const handleQuickStatusChange = async (taskId) => {
        
    };

    const filteredTasks = tasks.filter(task => {
        if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
        if (filterStatus !== 'all' && task.status !== filterStatus) return false;
        if (filterAssignee !== 'all') {
            // Check if task has this assignee
            const assigneeIds = task.assignee_ids || [];
            if (!assigneeIds.includes(filterAssignee)) return false;
        }
        if (selectedList && task.list_id !== selectedList.id) return false;
        return true;
    });

    const tasksByStatus = {};
    const statuses = selectedList?.statuses || [
        { name: 'To Do', color: '#9FB0AC' },
        { name: 'In Progress', color: '#6EC1E4' },
        { name: 'Review', color: '#F5C26B' },
        { name: 'Done', color: '#7ED957' },
    ];
    
    // Group tasks by status (case-insensitive matching)
    statuses.forEach(status => {
        const statusLower = status.name.toLowerCase();
        tasksByStatus[status.name] = filteredTasks.filter(t => 
            t.status?.toLowerCase() === statusLower
        );
    });

    if (initializing || !currentWorkspace) {
        return (
            <Layout title="Tasks">
                <div className="flex items-center justify-center h-64">
                    <div className="spinner" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Deleted Tasks" subtitle={currentSpace?.name || 'All tasks'}>
            <div className="flex h-[calc(100vh-120px)]" data-testid="tasks-page">
                {/* Main Content Area */}
                <div className={cn(
                    "flex-1 transition-all duration-300 overflow-auto",
                    isPanelOpen ? "mr-0" : ""
                )}>
                    <div className="space-y-4 p-0">
                        {/* Toolbar */}
                        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        {/* Space Selector */}
                        <Select 
                            value={currentSpace?.id || ''} 
                            onValueChange={(val) => {
                                const space = spaces.find(s => s.id === val);
                                selectSpace(space);
                            }}
                        >
                            <SelectTrigger className="w-48" data-testid="space-selector">
                                <SelectValue placeholder="Select Space" />
                            </SelectTrigger>
                            <SelectContent>
                                {spaces.map(space => (
                                    <SelectItem key={space.id} value={space.id}>
                                        <div className="flex items-center gap-2">
                                            <div 
                                                className="w-2 h-2 rounded-full"
                                                style={{ backgroundColor: space.color }}
                                            />
                                            {space.name}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        {/* List Selector with Add Button */}
                        <div className="flex items-center gap-1">
                            {lists.length > 0 ? (
                                <Select 
                                    value={selectedList?.id || ''} 
                                    onValueChange={(val) => {
                                        const list = lists.find(l => l.id === val);
                                        setSelectedList(list);
                                    }}
                                >
                                    <SelectTrigger className="w-40" data-testid="list-selector">
                                        <SelectValue placeholder="Select List" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {lists.map(list => (
                                            <SelectItem key={list.id} value={list.id}>
                                                {list.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <span className="text-sm text-brand-text-muted px-2">No lists <Link className="text-brand-primary border-b border-dashed border-brand-primary mx-2" to={`/admin/settings/?tab=lists&wsid=${currentWorkspace.id}`}>Go to Settings</Link> </span>
                            )}
                            
                            {/* Add/Manage List Button - only for non-client users */}
                            {/* {canEditTasks && (
                                <Button 
                                    variant="outline" 
                                    size="icon"
                                    className="h-9 w-9"
                                    onClick={() => { setEditingList(null); setIsListModalOpen(true); }}
                                    data-testid="manage-lists-btn"
                                >
                                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                                </Button>
                            )} */}
                        </div>

                        {/* Filters */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" data-testid="filter-btn">
                                    <Filter className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                    Filter
                                    {(filterPriority !== 'all' || filterAssignee !== 'all') && (
                                        <span className="ml-1 px-1.5 py-0.5 text-xs bg-brand-primary text-white rounded-full">
                                            {[filterPriority !== 'all', filterAssignee !== 'all'].filter(Boolean).length}
                                        </span>
                                    )}
                                    <ChevronDown className="w-4 h-4 ml-2" strokeWidth={1.5} />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-56">
                                <div className="p-2 space-y-3">
                                    <div>
                                        <p className="text-xs font-semibold text-brand-text-muted mb-2">Priority</p>
                                        <Select value={filterPriority} onValueChange={setFilterPriority}>
                                            <SelectTrigger className="h-8">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Priorities</SelectItem>
                                                <SelectItem value="urgent">Urgent</SelectItem>
                                                <SelectItem value="high">High</SelectItem>
                                                <SelectItem value="medium">Medium</SelectItem>
                                                <SelectItem value="low">Low</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-brand-text-muted mb-2">Assignee</p>
                                        <Select value={filterAssignee} onValueChange={setFilterAssignee}>
                                            <SelectTrigger className="h-8">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="all">All Members</SelectItem>
                                                {members.map(member => (
                                                    <SelectItem key={member.user_id} value={member.user_id}>
                                                        {member.user_name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {(filterPriority !== 'all' || filterAssignee !== 'all') && (
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="w-full text-brand-text-muted"
                                            onClick={() => {
                                                setFilterPriority('all');
                                                setFilterAssignee('all');
                                            }}
                                        >
                                            Clear Filters
                                        </Button>
                                    )}
                                </div>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    <div className="flex items-center gap-3">

                    </div>
                </div>

                {/* Content */}
                {!currentSpace ? (
                    <div className="empty-state">
                        <p className="text-brand-text-muted">Select a space to view tasks</p>
                    </div>
                ) : (
                    /* List View - Grouped by Status */
                    <div className="space-y-6" data-testid="task-list">
                        {statuses.map((statusObj, index) => {
                            const statusTasks = filteredTasks.filter(t => t.status === statusObj.name);
                            if (statusTasks.length === 0) return null;
                            return (
                                <Card key={statusObj.name} className={cn("border-brand-border overflow-hidden", index > 0 && "mt-2")}>
                                    <div 
                                        className="px-4 py-2 border-b border-brand-border flex items-center gap-2"
                                        style={{ backgroundColor: `${statusObj.color}20` }}
                                    >
                                        <div 
                                            className="w-3 h-3 rounded-full" 
                                            style={{ backgroundColor: statusObj.color }} 
                                        />
                                        <span className="font-medium text-sm">{statusObj.name}</span>
                                        <span className="text-xs text-brand-text-muted">({statusTasks.length})</span>
                                    </div>
                                    <div className="divide-y divide-brand-border">
                                        {statusTasks.map((task) => (
                                            <TaskListItem 
                                                key={task.id}
                                                task={task}
                                                statuses={statuses}
                                                members={members}
                                                onClick={() => handleTaskClick(task)}
                                                onStatusChange={() => {}}
                                                onStartTimer={() => {}}
                                                onStopTimer={() => {}}
                                                isTimerActive={activeTimer?.task_id === task.id}
                                            />
                                        ))}
                                    </div>
                                </Card>
                            );
                        })}
                        {filteredTasks.length === 0 && (
                            <Card className="border-brand-border p-8 text-center text-brand-text-muted">
                                No tasks found. Create your first task!
                            </Card>
                        )}
                    </div>
                )}
                    </div>
                </div>
            </div>

            {/* Task Detail Full Modal */}
            {selectedTask && isDetailModalOpen && (
                <TaskDetailFullModal
                    isOpen={isDetailModalOpen}
                    onClose={handleDetailModalClose}
                    task={selectedTask}
                    listId={selectedList?.id}
                    statuses={statuses}
                    members={members}
                    readOnly={true}
                    isDeletedTask={true}
                    onTaskUpdate={() => {
                        refreshDeletedTasks();
                    }}
                />
            )}
        </Layout>
    );
}

// Task List Item Component for List View
const TaskListItem = ({ task, statuses, members, onClick, onStatusChange, onStartTimer, onStopTimer, isTimerActive }) => {
    const priorityStyle = priorityConfig[task.priority];
    const overdue = isOverdue(task.due_date) && task.status !== 'Done';
    const assignees = task.assignees || [];

    return (
        <div 
            className={cn(
                "flex items-center gap-4 p-4 cursor-pointer transition-all",
                isTimerActive 
                    ? "bg-brand-primary/10 border-l-4 border-l-brand-primary animate-pulse" 
                    : "hover:bg-brand-accent-soft/30"
            )}
            onClick={onClick}
            data-testid={`task-list-item-${task.id}`}
        >

            {/* Task Code */}
            <span className="text-xs font-mono text-brand-primary font-semibold flex-shrink-0">
                #{task.task_code || task.task_number}
            </span>

            {/* Title */}
            <div className="flex-1 min-w-0">
                <p className={cn(
                    'font-medium text-sm truncate',
                    task.status === 'Done' ? 'text-brand-text-muted line-through' : 'text-brand-text-primary'
                )}>
                    {task.title}
                    {isTimerActive && (
                        <span className="ml-2 text-xs text-brand-primary font-normal">(Timer Running)</span>
                    )}
                </p>
            </div>

            {/* Priority */}
            {task.priority !== 'none' && (
                <span className={cn('text-xs px-2 py-1 rounded-full flex-shrink-0', priorityStyle.bgClass)}>
                    {priorityStyle.label}
                </span>
            )}

            {/* Status */}
            <Select 
                value={task.status} 
                onValueChange={(val) => {
                    onStatusChange(task.id, val);
                }}
            >
                <SelectTrigger 
                    className="w-32 h-8" 
                    onClick={(e) => e.stopPropagation()}
                >
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {statuses.map(s => (
                        <SelectItem key={s.name} value={s.name}>
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                                {s.name}
                            </div>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {/* Due Date */}
            {task.due_date && (
                <span className={cn(
                    'text-xs flex items-center gap-1 flex-shrink-0',
                    overdue ? 'text-brand-error' : 'text-brand-text-muted'
                )}>
                    <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {formatDate(task.due_date)}
                </span>
            )}

            {/* Assignee */}
            {assignees.length > 0 && (
                <div className="avatar w-7 h-7 text-xs flex-shrink-0" title={assignees[0].name}>
                    {getInitials(assignees[0].name)}
                </div>
            )}
        </div>
    );
};
